import React, { useState, useEffect, useRef } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import PopupAlert from '../../../../common/Alert/PopupAlert';
import { LENGTH_RANGE, TEXT_PATTERN, URL_PATTERN, CLIENT_CRED_LENGTH } from '../../../../common/base/constants';

function EditOrCreateApplication({ data = null, className, show, onClose, onSubmit, type, showAlert = false, status="", message="", title="", visibility, gatewayName, allowCustomClientCredentials }) {
    const isClientCredentialGenerationEnabled = allowCustomClientCredentials === true || allowCustomClientCredentials === 'true';
    const isGatewayNotMashery = gatewayName?.toUpperCase() !== 'NG_MASHERY';
    const shouldShowClientCredentialFields = isGatewayNotMashery && isClientCredentialGenerationEnabled;
    const grantTypesRef = useRef();
    const clientAppNameRef = useRef();
    const clientAppDescriptionRef = useRef();
    const applicationURLRef = useRef();
    const redirectUrisRef = useRef();
    const clientIdRef = useRef();
    const clientSecretRef = useRef();
    const redirectUrisEditRef = useRef();
    const [formValues, setFormValues] = useState({
        clientAppName: '',
        clientAppDescription: '',
        applicationURL: '',
        grantTypes: [],
        applicationType: "",
        redirectUris: [],
        clientId: '',
        clientSecret: '', 
        oidcType: "",
        useExistingClient: false
    });
    const [isFormValid, setIsFormValid] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [isRedirectUriRequired, setIsRedirectUriRequired] = useState(false);
    const [touched, setTouched] = useState({});
    const [descriptionLength, setDescriptionLength] = useState(0);
    const [appTypeNote, setAppTypeNote] = useState("");
    const [useExistingClient, setUseExistingClient] = useState(false);
    const [showClientSecret, setShowClientSecret] = useState(false);
    const [isFormDirty, setIsFormDirty] = useState(false);
    // const grantTypeMap = {
    //     'Authorization Code Grant': 'authorization_code',
    //     'Client Credential Grant': 'client_credentials',
    //     'Resource Owner Grant': 'resource_owner_credentials',
    //     'Implicit Grant': 'implicit',
    //     'Refresh Token': 'refresh_token'
    // };

    const HBR_INPUT_LABEL_HEIGHT_PLUS_MARGIN = 25;
    const HBR_INPUT_FIELD_HEIGHT = 40;

    const applicationTypeMap = {
        "Native Application" : { 
            value: "native", 
            desc: "Desktop or mobile applications that run natively on a device and redirect users to a non-HTTP callback (for example, iOS, Android, React Native)",
            grantTypes: "Authorization Code PKCE, Device Code and Refresh Token",
            type: "OIDC_NATIVE",
            grantValues: ["AUTHORIZATION_CODE_PKCE", "DEVICE_CODE", "REFRESH_TOKEN"]
        },
        "Single Page App": { 
            value: "spa", 
            desc: "Single-page web applications that run in the browser where the client receives tokens (for example, Javascript, Angular, React, Vue)",
            grantTypes: "Authorization Code PKCE and Refresh Token",
            type: "OIDC_SPA",
            grantValues: ["AUTHORIZATION_CODE_PKCE", "REFRESH_TOKEN"]
        },
        "Web App": { 
            value: "web", 
            desc: "Server-side applications where authentication and tokens are handled on the server (for example, Go, Java, ASP.Net, Node.js, PHP) OR This could be a SAML Application",
            grantTypes: "Authorization Code and Refresh Token",
            type: "OIDC_WEB",
            grantValues: ["AUTHORIZATION_CODE", "REFRESH_TOKEN"]
        },
        "API": { 
            value: "api", 
            desc: "Your Application Intends to consume Okta/CCI API’s",
            grantTypes: "Client Credentials",
            type: "OIDC_OAUTH_CLIENT",
            grantValues: ["CLIENT_CREDENTIALS"]
        }
    }

    const syncHbrField = (fieldRef, { value, helpText, error }) => {
        const field = fieldRef.current;

        if (!field) {
            return;
        }

        if (value !== undefined && field.value !== value) {
            field.value = value;
        }

        if (helpText) {
            field.setAttribute('help-text', helpText);
        } else {
            field.removeAttribute('help-text');
        }

        if (error) {
            field.setAttribute('error', 'true');
        } else {
            field.removeAttribute('error');
        }
    };
    
    useEffect(() => {
        if (data) {
            setFormValues(data);
            setDescriptionLength(data.clientAppDescription.length);
            setTouched({});
            setFormErrors({});
            setIsFormDirty(false);
        }
    }, [data]);

    function handleClose() {
        onClose();
    }

    function handleInputChange(e) {
        const { name, value } = e.target;
        if(!name) return;
        if (name === 'redirectUris') {
            setFormValues(prevValue => ({...prevValue, [name]: value.split(',').map(uri => uri.trim()).filter(uri => uri)}));
        }else {
            let _value = value;
            if(name === 'grantTypes' && value.includes('Refresh Token')) {
                const isRequiredSelected = value.includes('Authorization Code Grant') || value.includes('Resource Owner Grant');
                if(!isRequiredSelected) {
                    _value = value.filter(gt => gt !== 'Refresh Token');
                    grantTypesRef.current.value = !_value.length ? null : _value;
                }
            }
            setFormValues(prevValue => ({...prevValue, [name]: _value}));
        }

        if (name === 'clientAppDescription') {
            setDescriptionLength(value.length);
        }
        setTouched(prevValue => ({
            ...prevValue,
            [name]: true
        }));
        if (type === "Edit") {
            setIsFormDirty(true);
        }
    };

    function checkDuplicate(list, errors) {
        const obj = {};
        for(let i=0; i<list.length; i++) {
            if(obj[list[i]]){
                errors.redirectUris = "Duplicate URIs found!"
                break;
            }else {
                obj[list[i]] = 1;
            }
        }

    }

    function validateForm() {
        const errors = {};

        //Application name validation
        if (!formValues.clientAppName.trim()) {
            errors.clientAppName = 'Application name is required';
        }
        if (formValues.clientAppName && !TEXT_PATTERN.test(formValues.clientAppName)) {
            errors.clientAppName = 'Application name must not have special characters';
        }
        if (formValues.clientAppName && formValues.clientAppName.length < LENGTH_RANGE.MIN) {
            errors.clientAppName = `Application name must be at least ${LENGTH_RANGE.MIN} characters`;
        }

        //Application description validation
        if (formValues.clientAppDescription && !TEXT_PATTERN.test(formValues.clientAppDescription)) {
            errors.clientAppDescription = 'Description must not have special characters';
        }

        //Application URL validation
        if (formValues.applicationURL && !URL_PATTERN.test(formValues.applicationURL)) {
            errors.applicationURL = 'Application URL must be a valid URL';
        }    

        //Application Type Validation
        if(type === "Create" && !useExistingClient && !formValues.applicationType.trim()) {
            errors.clientAppType = "Application Type is required";
        }

        //Grant types validation
        if (type === "Create" && !useExistingClient && formValues.grantTypes.length === 0) {
            errors.grantTypes = 'At least one grant type is required';
        }

        //Redirect URIs validation
        if (type === "Create" && (formValues.applicationType === "native" ||
            formValues.applicationType === "spa" || 
            formValues.applicationType === "web")) {
            if (formValues.redirectUris.length == 0) {
                errors.redirectUris = 'Redirect URI is required';
            }  
        }

        if (type === "Edit" && !formValues.grantTypes.includes("CLIENT_CREDENTIALS") && formValues.redirectUris.length == 0) {
            errors.redirectUris = 'Redirect URI is required';
        }

        if(formValues.redirectUris.length > 0) {
            checkDuplicate(formValues.redirectUris || [], errors);
        }

        // if (formValues.grantTypes.includes('Refresh Token') &&
        //     !formValues.grantTypes.includes('Authorization Code Grant') &&
        //     !formValues.grantTypes.includes('Resource Owner Grant')) {
        //     errors.grantTypes = 'Refresh Token requires Authorization Code Grant or Resource Owner Grant';
        // }  

        //Redirect URI validation
        if (formValues.redirectUris.length > 0 && formValues.redirectUris[0]) {
            formValues.redirectUris.forEach(uri => {
                if (!URL_PATTERN.test(uri)) {
                    errors.redirectUris = 'Redirect URI must be a valid URL';
                }
            });
        }  
        
        // Client ID Validation
        if (type === "Create" && shouldShowClientCredentialFields && !formValues.clientId.trim()) {
            errors.clientId = 'Client ID is required';
        }
        
        if (type === "Create" && shouldShowClientCredentialFields && formValues.clientId.trim() && formValues.clientId.trim().length < CLIENT_CRED_LENGTH) {
            errors.clientId = `Client ID must be at least ${CLIENT_CRED_LENGTH} characters`;
        }
        // Client Secret validation
        if (type === "Create" && shouldShowClientCredentialFields && formValues.applicationType !== "spa" && !formValues.clientSecret.trim()) {
            errors.clientSecret = 'Client Secret is required';
        }

        if (type === "Create" && shouldShowClientCredentialFields && formValues.applicationType !== "spa" && formValues.clientSecret.trim() && formValues.clientSecret.trim().length < CLIENT_CRED_LENGTH) {
            errors.clientSecret = `Client Secret must be at least ${CLIENT_CRED_LENGTH} characters`;
        }
        
        setFormErrors(errors);
        const noErrors = Object.keys(errors).length === 0;
        setIsFormValid(type === "Edit" ? noErrors && isFormDirty : noErrors);
    }

    useEffect(() => {
        // const isRequired = formValues.grantTypes.includes('Authorization Code Grant') ||
        //                    formValues.grantTypes.includes('Implicit Grant');
        // setIsRedirectUriRequired(isRequired);
        validateForm();
        // if(formValues.grantTypes && grantTypesRef.current) {
        //     grantTypesRef.current.value = formValues.grantTypes;
        // }
    }, [formValues, isFormDirty]);

    useEffect(() => {
        const select = grantTypesRef.current;
    
        if (!select) {
          return;
        }
        
        if (!show) {
            select.removeEventListener('hbr-change', handleInputChange);
        } else {
            select.addEventListener('hbr-change', handleInputChange);
        }
    }, [show]);

    useEffect(() => {
        syncHbrField(clientAppNameRef, {
            value: formValues.clientAppName,
            helpText: formErrors.clientAppName,
            error: Boolean(formErrors.clientAppName && touched.clientAppName)
        });
    }, [formErrors.clientAppName, formValues.clientAppName, show, touched.clientAppName]);

    useEffect(() => {
        syncHbrField(clientAppDescriptionRef, {
            value: formValues.clientAppDescription,
            helpText: formErrors.clientAppDescription && touched.clientAppDescription
                ? formErrors.clientAppDescription
                : `${descriptionLength} / ${LENGTH_RANGE.MAX} characters used`,
            error: Boolean(formErrors.clientAppDescription && touched.clientAppDescription)
        });
    }, [descriptionLength, formErrors.clientAppDescription, formValues.clientAppDescription, show, touched.clientAppDescription]);

    useEffect(() => {
        syncHbrField(applicationURLRef, {
            value: formValues.applicationURL,
            helpText: formErrors.applicationURL,
            error: Boolean(formErrors.applicationURL && touched.applicationURL)
        });
    }, [formErrors.applicationURL, formValues.applicationURL, show, touched.applicationURL]);

    useEffect(() => {
        const value = formValues.redirectUris[0] !== ''
            ? (formValues.redirectUris.length > 1 ? formValues.redirectUris.join(', ') : formValues.redirectUris[0])
            : '';

        syncHbrField(redirectUrisRef, {
            value,
            helpText: formErrors.redirectUris || '',
            error: Boolean(formErrors.redirectUris && touched.redirectUris)
        });
    }, [formErrors.redirectUris, formValues.redirectUris, show, touched.redirectUris]);

    useEffect(() => {
        const value = formValues.redirectUris[0] !== ''
            ? (formValues.redirectUris.length > 1 ? formValues.redirectUris.join(', ') : formValues.redirectUris[0])
            : '';

        syncHbrField(redirectUrisEditRef, {
            value,
            helpText: formErrors.redirectUris || '',
            error: Boolean(formErrors.redirectUris && touched.redirectUris)
        });
    }, [formErrors.redirectUris, formValues.redirectUris, show, touched.redirectUris]);

    useEffect(() => {
        syncHbrField(clientIdRef, {
            value: formValues.clientId,
            helpText: formErrors.clientId,
            error: Boolean(formErrors.clientId && touched.clientId)
        });
    }, [formErrors.clientId, formValues.clientId, show, touched.clientId]);

    useEffect(() => {
        syncHbrField(clientSecretRef, {
            value: formValues.clientSecret,
            helpText: formErrors.clientSecret,
            error: Boolean(formErrors.clientSecret && touched.clientSecret)
        });
    }, [formErrors.clientSecret, formValues.clientSecret, show, touched.clientSecret]);

    function handleSubmit() {
        const visibilityArray = visibility ? JSON.parse(visibility) : [];
        const convertedFormValues = {
            ...formValues,
            //grantTypes: formValues.grantTypes.map(grantType => grantTypeMap[grantType].toUpperCase()),
            ...(visibilityArray.some(item => item.constraint === "accessLevel")
                ? { accessLevel: visibilityArray.find(item => item.constraint === "accessLevel").value }
            : {})
        };
        onSubmit(convertedFormValues);
    }

    // const isRefreshTokenDisabled = () => {
    //     return !(formValues.grantTypes.includes('Authorization Code Grant') ||
    //         formValues.grantTypes.includes('Resource Owner Grant'));
    // };

    const isRedirectUrlRequired = () => {
        return formValues.applicationType === "native" ||
        formValues.applicationType === "spa" || 
        formValues.applicationType === "web";
    }

    const changeApplicationType = (value) => {
        console.log("Clicked value:", value);
        setFormValues((prevValues) => ({
            ...prevValues,
            applicationType: value
        }));
        isRedirectUrlRequired();
    };

    useEffect(() => {
        if (formValues.applicationType) {
            console.log("Application Type changed to:", formValues.applicationType);
            const appTypeData = (() => {
                for (const [key, data] of Object.entries(applicationTypeMap)) {
                    if (data.value === formValues.applicationType) {
                        return data; 
                    }
                }
                return null; 
            })();
            if(appTypeData) {
                setFormValues((prevValues) => ({
                    ...prevValues,
                    grantTypes: appTypeData.grantValues,
                    oidcType: appTypeData.type
                }));
                setAppTypeNote(" Grant types: " + appTypeData.grantTypes);
            } else {
                setAppTypeNote("");
            }
            if(formValues.applicationType === "api") {
                formValues.redirectUris = [];
            }
            if(formValues.applicationType === "spa") {
                formValues.clientSecret = "";
            }
        }
        console.log(formValues);
    }, [formValues.applicationType]);

    const handleAnswerChange = (e) => {
        console.log("CHECKED VALUE" + e.target);
        setUseExistingClient(e.target.value === "true");
        const val = e.target.value === "true" ? true: false;
        setFormValues(prevFormValues => ({
            ...prevFormValues, 
            applicationType: "",
            grantTypes: [],
            clientId: "",
            clientSecret: "",
            useExistingClient: val
        }));
        setAppTypeNote("");
        setTouched({ ...touched, clientSecret: false, clientId: false });
        const updatedErrors = { ...formErrors };
        delete updatedErrors.clientId;
        delete updatedErrors.clientSecret;
        setFormErrors(updatedErrors);
    }

    const generateClientIdSecret = () => {
        let uniqueClientId;
        let uniqueClientSecret;
        if(formValues.applicationType === "spa") {
            uniqueClientId = crypto.randomUUID();
            uniqueClientSecret = "";
        } else {
            uniqueClientId = crypto.randomUUID();
            uniqueClientSecret = crypto.randomUUID();
        }
        setFormValues(prevFormValues => ({
            ...prevFormValues, 
            clientId: uniqueClientId,
            clientSecret: uniqueClientSecret
        }));
    }

    const toggleClientSecretVisibility = () => {
        setShowClientSecret(prev => !prev);
    };

    const handleClientCredChange = (e) => {
        const { name, value } = e.target;
        setFormValues(prevValues => ({
            ...prevValues,
            [name]: value
        }));
        setTouched(prevValue => ({
            ...prevValue,
            [name]: true
        }));
    };

    return (
        <Modal show={show} centered="true" onHide={handleClose} className={`app-modal-popup ${className || ''}`} backdrop='static'>
            <Modal.Header closeButton>
                <Modal.Title>{type} Application</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {showAlert && (
                    <PopupAlert status={status} message={message} title={title} />
                )}
                <hbr-form autocomplete="off">
                    <hbr-input
                        ref={clientAppNameRef}
                        label="Application name" 
                        placeholder="Application name" 
                        type="text"
                        name="clientAppName"
                        onInput={handleInputChange}
                        required
                        disabled={type === "Edit"}
                        onBlur={() => setTouched({ ...touched, clientAppName: true })}
                        maxLength={LENGTH_RANGE.MAX}
                    ></hbr-input>
                    
                    <hbr-textarea
                        ref={clientAppDescriptionRef}
                        onInput={handleInputChange}
                        label="Application Description"
                        name="clientAppDescription"
                        placeholder="Application Description"
                        onBlur={() => setTouched({ ...touched, clientAppDescription: true })}
                        maxLength={LENGTH_RANGE.MAX}
                    ></hbr-textarea>

                    <hbr-input
                        ref={applicationURLRef}
                        label="Application URL" 
                        placeholder="Application URL" 
                        type="text"
                        name="applicationURL"
                        onInput={handleInputChange}
                        onBlur={() => setTouched({ ...touched, applicationURL: true })}
                    ></hbr-input>

                    { type === "Create" && (
                        <div>
                            {/* <input
                                id="useExistingClient"
                                type="radio"
                                className="entry-modal-question-checkbox"
                                name="clientOption"
                                value="true"
                                onChange={handleAnswerChange}
                                checked={useExistingClient === true}
                                style={{marginRight: "5px"}}
                            >
                            </input>
                            <label htmlFor="useExistingClient" className="entry-modal-question-label checkBoxTitle"> Use Existing Okta Client?         </label>

                            <input
                                id="newClient"
                                type="radio"
                                className="entry-modal-question-checkbox"
                                name="clientOption"
                                value="false"
                                onChange={handleAnswerChange}
                                checked={useExistingClient === false}
                                style={{marginRight: "5px", marginLeft: "15px"}}
                            >
                            </input>
                            <label htmlFor="newClient" className="entry-modal-question-label checkBoxTitle"> Create New Client? </label> */}

                            {!useExistingClient && (
                                <div>
                                    <div className='appTypeTitle'>Application Type 
                                        <span style={{ color: "red" }}> *</span>
                                    </div>
                                    <div className='appTypeBody'>
                                        {Object.keys(applicationTypeMap).map((key, i) => (
                                            <hbr-tooltip size="small" content={applicationTypeMap[key].desc} 
                                                trigger="hover" placement="top" class="atm-tooltip--small">
                                                <Button variant="primary"
                                                    className={`appTypes ${formValues.applicationType === applicationTypeMap[key].value ? "appTypeSelected" : "appTypeDeselected"}`}
                                                    key={key} 
                                                    value={applicationTypeMap[key]} 
                                                    onClick={() => changeApplicationType(applicationTypeMap[key].value)}
                                                >
                                                    {key}
                                                </Button>
                                            </hbr-tooltip>
                                        ))}
                                    </div>
                                    { appTypeNote != "" && <div className='appNote'>
                                        <i class="atm-icon info-circle"></i>{appTypeNote}</div> 
                                    }

                                    { isRedirectUrlRequired() && (
                                        <hbr-input
                                            ref={redirectUrisRef}
                                            label="Redirect URI" 
                                            placeholder="Redirect URI" 
                                            type="text"
                                            required={isRedirectUrlRequired()}
                                            name="redirectUris"
                                            onInput={handleInputChange}
                                            //disabled={type === "Edit"}
                                            onBlur={() => setTouched({ ...touched, redirectUris: true })}
                                        ></hbr-input>
                                    )}
                                </div>
                            )}
                        
                            { gatewayName?.toUpperCase() !== 'NG_MASHERY' && allowCustomClientCredentials && (
                            <div>
                                <div style={{ fontSize: "12px", fontWeight: "bold"}}><i class="atm-icon info-circle" style={{marginRight: "5px"}}></i>Create new credentials only, existing Client ID and Secret are not permitted.</div>
                                <hbr-input
                                    ref={clientIdRef}
                                    label="New Client ID" 
                                    placeholder="Client ID" 
                                    type="text"
                                    name="clientId"
                                    required
                                    onBlur={() => setTouched({ ...touched, clientId: true })}
                                    onInput={handleClientCredChange}
                                ></hbr-input>

                                <div style={{ position: 'relative', marginBottom: '15px' }}>
                                    <hbr-input
                                        ref={clientSecretRef}
                                        label="New Client Secret" 
                                        placeholder="Client Secret" 
                                        //type="password"
                                        type={showClientSecret ? "text" : "password"}
                                        name="clientSecret"
                                        required={formValues.applicationType != "spa"}
                                        onBlur={() => setTouched({ ...touched, clientSecret: true })}
                                        onInput={handleClientCredChange}
                                        disabled={!useExistingClient && formValues.applicationType === "spa"}
                                    ></hbr-input>

                                    { formValues.clientSecret && formValues.clientSecret.length > 0 && (
                                        <span
                                            onClick={toggleClientSecretVisibility}
                                            style={{
                                                position: 'absolute',
                                                top: `${HBR_INPUT_LABEL_HEIGHT_PLUS_MARGIN + (HBR_INPUT_FIELD_HEIGHT / 2)}px`,
                                                right: '10px',
                                                transform: 'translateY(-50%)',
                                                cursor: 'pointer',
                                                zIndex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: '24px',
                                                width: '24px',
                                            }}
                                            >
                                            {showClientSecret ? (
                                                <i className="me-2 ps-2 atm-icon eye-closed"></i>
                                            ) : (
                                                <i className="me-2 ps-2 atm-icon eye"></i>
                                            )}
                                        </span>
                                    )}
                                </div>
                            </div>
                            )}

                            {shouldShowClientCredentialFields && !useExistingClient && formValues.applicationType !== "spa" && (
                                <Button onClick={generateClientIdSecret}>
                                    Generate Client ID & Secret
                                </Button>
                            )}
                            {shouldShowClientCredentialFields && !useExistingClient && formValues.applicationType === "spa" && (
                            <Button onClick={generateClientIdSecret} >
                                    Generate Client ID
                                </Button> 
                            )}
                        </div>
                    )}

                    { type === "Edit" && !formValues.grantTypes.includes("CLIENT_CREDENTIALS") && (
                        <hbr-input
                            ref={redirectUrisEditRef}
                            label="Redirect URI" 
                            placeholder="Redirect URI" 
                            type="text"
                            required
                            name="redirectUris"
                            onInput={handleInputChange}
                            //disabled={type === "Edit"}
                            onBlur={() => setTouched({ ...touched, redirectUris: true })}
                        ></hbr-input>
                    )}

                </hbr-form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    type="submit"
                    onClick={handleSubmit}
                    disabled={!isFormValid}
                >
                    Save Changes
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default EditOrCreateApplication;
